import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { DeliveryCity, GetDeliveryCitiesParams, GetDeliveryCitiesResponse } from '../types/delivery-city.type';
import { ADMIN_DELIVERY_CITIES_API_PATHS } from '../constants/delivery-cities-api-paths.constants';

export type GetDeliveryCitiesRequest = GetDeliveryCitiesParams;

type UseAdminGetDeliveryCitiesResult = {
  isLoading: boolean;
  isRefetching: boolean;
  totalCount: number;
  deliveryCities: DeliveryCity[];
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetDeliveryCitiesResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  totalCount: 0,
  deliveryCities: [],
};

export function useAdminGetDeliveryCities({
  page,
  limit,
  sortBy,
  sortDirection,
  filters,
}: GetDeliveryCitiesRequest): UseAdminGetDeliveryCitiesResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const serializedFilters = JSON.stringify(filters);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<GetDeliveryCitiesResponse>(
        ADMIN_DELIVERY_CITIES_API_PATHS.GET_DELIVERY_CITIES,
        { params: { page, limit, sortBy, sortDirection, filters: serializedFilters } }
      );

      setResult({
        isLoading: false,
        isRefetching: false,
        totalCount: response.totalCount,
        deliveryCities: response.deliveryCities,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(
          error,
          'Une erreur est survenue lors de la récupération des villes de livraison'
        ),
        { variant: 'error' }
      );
      setResult({ ...INITIAL_STATE, isLoading: false });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
